export function buildItems(rows) {
  const merged = new Map();
  for (const row of rows) {
    const rawId = String(row.id ?? '');
    const id = Number(rawId);
    const quantity = Number(row.quantity);
    if (!/^\d+$/.test(rawId) || !Number.isSafeInteger(id) || id < 1) throw new Error('variant');
    if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('quantity');
    const total = (merged.get(id) || 0) + quantity;
    if (!Number.isSafeInteger(total)) throw new Error('quantity');
    merged.set(id, total);
  }
  if (!merged.size) throw new Error('empty');
  return Array.from(merged, ([id, quantity]) => ({ id, quantity }));
}

export class CartSubmission {
  constructor({ request, addUrl, cartUrl, navigate }) {
    this.request = request;
    this.addUrl = addUrl;
    this.cartUrl = cartUrl;
    this.navigate = navigate;
    this.state = 'idle';
  }
  async submit(rows) {
    if (this.state !== 'idle') throw new Error('already_submitted');
    const items = buildItems(rows);
    this.state = 'sending';
    try {
      const response = await this.request(this.addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) throw new Error('unconfirmed');
      const result = await response.json();
      const ids = new Set((result.items || []).map(item => String(item.variant_id ?? item.id)));
      if (items.some(item => !ids.has(String(item.id)) || Number(result.items.find(added => String(added.variant_id ?? added.id) === String(item.id))?.quantity) < item.quantity)) throw new Error('unconfirmed');
      this.state = 'confirmed';
      this.navigate(this.cartUrl);
      return result;
    } catch {
      this.state = 'unconfirmed';
      throw new Error('unconfirmed');
    }
  }
}

if (typeof window !== 'undefined' && window.customElements && !customElements.get('nla-roots-bundle')) {
  class RootsBundle extends HTMLElement {
    connectedCallback() {
      const generation = this._generation = (this._generation || 0) + 1;
      queueMicrotask(() => { if (this.isConnected && this._generation === generation) this.mount(); });
    }
    mount() {
      this._controller?.abort();
      this._observer?.disconnect();
      const controller = this._controller = new AbortController();
      const signal = controller.signal;
      const button = this.querySelector('[data-bundle-add]');
      const status = this.querySelector('[data-bundle-status]');
      const count = this.querySelector('[data-bundle-count]');
      const checkCart = this.querySelector('[data-bundle-check-cart]');
      const cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
      if (!button || !status) return;
      const initialDisabled = new Map(Array.from(this.querySelectorAll('input, select, button'), control => [control, control.disabled]));
      const submission = new CartSubmission({
        request: window.fetch.bind(window),
        addUrl: this.dataset.addUrl,
        cartUrl: this.dataset.cartUrl,
        navigate: url => window.location.assign(url)
      });
      const selected = () => cards.filter(card => card.querySelector('[data-bundle-pick]')?.checked);
      const rows = () => selected().map(card => ({
        id: card.querySelector('[name="id"]').value,
        quantity: card.querySelector('[name="quantity"]').value
      }));
      const refresh = () => {
        if (submission.state !== 'idle') return;
        let total = 0;
        try {
          total = buildItems(rows()).reduce((sum, item) => sum + item.quantity, 0);
          button.disabled = false;
          status.textContent = '';
        } catch (error) {
          button.disabled = true;
          status.textContent = error.message === 'empty' ? '' : this.dataset.quantityError;
        }
        count.textContent = this.dataset.countLabel.replace('[count]', String(total));
        const progress = this.querySelector('[data-bundle-progress]');
        if (progress && this.dataset.discountEligible === 'true') {
          progress.hidden = false;
          progress.querySelectorAll('.roots-bundle__steps span').forEach((step, index) => step.classList.toggle('is-filled', index < Math.min(total, 4)));
          const label = progress.querySelector('[data-bundle-progress-label]');
          label.textContent = total >= 4 ? this.dataset.progressReady : total > 0 ? this.dataset.progressRemaining.replace('[remaining]', String(4 - total)) : this.dataset.progressEmpty;
        }
        cards.forEach(card => card.classList.toggle('is-selected', Boolean(card.querySelector('[data-bundle-pick]')?.checked)));
      };
      this.addEventListener('change', event => {
        const select = event.target.closest('[data-bundle-variant]');
        if (select) {
          const card = select.closest('[data-bundle-card]');
          const option = select.selectedOptions[0];
          const price = card.querySelector('[data-bundle-price]');
          const image = card.querySelector('img.roots-bundle__image');
          const missing = card.querySelector('[data-bundle-missing-image]');
          if (price) price.textContent = option.dataset.price;
          if (image) {
            const src = option.dataset.image;
            image.hidden = !src;
            if (src) {
              image.removeAttribute('srcset');
              image.src = src;
            }
            if (missing) missing.hidden = Boolean(src);
          }
        }
        refresh();
      }, { signal });
      this.addEventListener('input', refresh, { signal });
      this.addEventListener('submit', event => {
        if (submission.state !== 'idle') event.preventDefault();
      }, { signal });
      button.addEventListener('click', async () => {
        if (submission.state !== 'idle' || button.disabled) return;
        const chosen = selected();
        if (chosen.some(card => !card.querySelector('[name="quantity"]').reportValidity())) return;
        let picked;
        try { picked = buildItems(rows()); } catch { refresh(); return; }
        const controls = Array.from(this.querySelectorAll('input, select, button'));
        controls.forEach(control => { control.disabled = true; });
        this.setAttribute('aria-busy', 'true');
        status.textContent = this.dataset.sendingLabel;
        try {
          await submission.submit(picked);
          status.textContent = this.dataset.confirmedLabel;
        } catch {
          status.textContent = this.dataset.unconfirmedLabel;
          checkCart.hidden = false;
          checkCart.focus();
        } finally {
          this.setAttribute('aria-busy', 'false');
        }
      }, { signal });
      cards.forEach(card => {
        const image = card.querySelector('img.roots-bundle__image');
        image?.addEventListener('error', () => {
          image.hidden = true;
          const missing = card.querySelector('[data-bundle-missing-image]');
          if (missing) missing.hidden = false;
        }, { signal });
      });
      window.addEventListener('pageshow', event => {
        if (!event.persisted || !['confirmed', 'unconfirmed'].includes(submission.state)) return;
        initialDisabled.forEach((disabled, control) => { control.disabled = disabled; });
        cards.forEach(card => { const pick = card.querySelector('[data-bundle-pick]'); if (pick) pick.checked = false; });
        checkCart.hidden = true;
        this.mount();
      }, { signal });
      this.querySelectorAll('[data-bundle-enhanced]').forEach(node => { node.hidden = false; });
      this.classList.add('is-enhanced');
      const media = window.matchMedia('(prefers-reduced-motion: reduce)');
      if ('IntersectionObserver' in window && !media.matches) {
        this._observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            this._observer.unobserve(entry.target);
          }
        }), { threshold: .1 });
        cards.forEach(card => this._observer.observe(card));
      } else cards.forEach(card => card.classList.add('is-revealed'));
      refresh();
    }
    disconnectedCallback() {
      this._generation = (this._generation || 0) + 1;
      this._controller?.abort();
      this._observer?.disconnect();
    }
  }
  customElements.define('nla-roots-bundle', RootsBundle);
}

